/**
 * PreviewLayerSection — one Paths / Landers / Offers list block of the
 * compact preview panel. Extracted from PreviewCompactLayersPanel.
 *
 * When `groups` is supplied (paths, viewing all pools) the rows are split into
 * one bordered block per allocation pool instead of a single flat list.
 *
 * For Media Devoted | Created By Ciro Autiero
 */

import { FC } from 'react';
import type { FlowOptimizerPreviewResult } from '@trafficpulse/shared';

import PreviewPathListRow from '../PreviewPathListRow';
import PreviewLayerLabels from '../PreviewLayerLabels';
import PreviewPoolGroupBlock from './PreviewPoolGroupBlock';
import { previewItemKey } from '../preview-sort';
import { toPreviewRow } from '../preview-row.types';
import type { PreviewPoolGroup } from '../preview-pool-groups';

export interface PreviewLayerSectionProps {
  label: string;
  /** Pool name shown next to the label when the list is pool-scoped. */
  poolLabel?: string;
  items: FlowOptimizerPreviewResult['items'];
  ranks: ReadonlyMap<string, number>;
  filteredByPath: boolean;
  onSelectPath?: (id: string) => void;
  selectedPathId: string | null;
  /** One block per pool; falls back to a flat list when omitted. */
  groups?: PreviewPoolGroup[] | null;
}

const PreviewLayerSection: FC<PreviewLayerSectionProps> = ({
  label,
  poolLabel,
  items,
  ranks,
  filteredByPath,
  onSelectPath,
  selectedPathId,
  groups,
}) => (
  <section className="space-y-1">
    <div className="flex items-baseline justify-between gap-2 px-0.5">
      <h4 className="text-[10px] font-semibold uppercase tracking-wide text-foreground">
        {label}
        {poolLabel ? (
          <span className="ml-1 text-[9px] font-normal normal-case text-muted-foreground">
            · {poolLabel}
          </span>
        ) : null}
        {filteredByPath ? (
          <span className="ml-1 text-[9px] font-normal text-muted-foreground">
            (filtered)
          </span>
        ) : null}
      </h4>
      <span className="text-[10px] tabular-nums text-muted-foreground">
        {items.length}
      </span>
    </div>
    {items.length === 0 ? (
      <div className="rounded-md border border-border/60">
        <p className="px-2 py-1.5 text-[11px] italic text-muted-foreground">
          {filteredByPath
            ? `No ${label.toLowerCase()} under the selected path.`
            : `No ${label.toLowerCase()} in preview.`}
        </p>
      </div>
    ) : groups && groups.length > 0 ? (
      <div className="space-y-2">
        {groups.map((group, i) => (
          <PreviewPoolGroupBlock
            key={group.id}
            name={group.name}
            items={group.items}
            ranks={ranks}
            onSelectPath={onSelectPath}
            selectedPathId={selectedPathId}
            hideLabels={i > 0}
          />
        ))}
      </div>
    ) : (
      <div className="overflow-hidden rounded-md border border-border/60">
        <PreviewLayerLabels />
        {items.map((item) => (
          <PreviewPathListRow
            key={previewItemKey(item)}
            name={item.itemName?.trim() || item.itemId}
            row={toPreviewRow(item)}
            rank={ranks.get(previewItemKey(item))}
            selected={onSelectPath != null && selectedPathId === item.itemId}
            onSelect={onSelectPath ? () => onSelectPath(item.itemId) : undefined}
          />
        ))}
      </div>
    )}
  </section>
);

export default PreviewLayerSection;
